import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { formatName } from "utils/index";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useUpdateProject } from "@/modules/work/projects/project/crud/update/mutation";
import { eventBus } from "utils/eventBus";
import { UserFieldInput } from "permissions/components/user-field-input";
import ProjectNameCell from "@/modules/work/projects/cells/name";
import { ROUTE } from "@/lib/utils";

export default function ProjectCoordinatorList() {
  const { employeeId } = useParams();
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };
  const permissions = usePermissions();
  const canReadClient = permissions.hasCollectionPermission("clients", "read");
  const canEditCoordinator = permissions.hasFieldAccess("projects", "update", "coordinator");

  const storageKey = employeeId ? `profile-pc-projects-${employeeId}` : "profile-pc-projects";

  const updateProjectMutation = useUpdateProject();

  // Listen for project refresh when project coordinator is updated
  // Note: useUpdateProject onSuccess calls invalidateAllProjectQueries()
  const handleUpdateSuccess = () => {
    eventBus.emit("refresh:list", `projects${storageKey}`);
  };

  const baseFields = [
    "id",
    "name",
    "project_id",
    "logo",
    "status",
    "coordinator.id",
    "coordinator.first_name",
    "coordinator.last_name",
    "coordinator.avatar",
  ];


  const fields = [
    ...baseFields,
    ...(canReadClient
      ? ["client.first_name", "client.last_name", "client.company_id.name"]
      : []),
  ];

  const columns = [
    {
      id: "name",
      header: "Project",
      accessorKey: "name",
      cell: ProjectNameCell
    },
    {
      id: "client",
      header: "Client",
      accessorKey: "client",
      collection: "clients",
      cell: ({ getValue }) => formatName(getValue()),
    },
    {
      id: "coordinator",
      header: "Coordinator",
      hidden: !canEditCoordinator,
      href: false,
      cell: ({ row }) => {
        const project = row.original;
        return (
          <UserFieldInput
            targetItem={project.id}
            field="coordinator"
            collection="projects"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="projects_coordinating"
              value={project.coordinator}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateProjectMutation.mutateAsync({
                        projectId: project.id,
                        values: {
                          coordinator: selectedEmployee,
                        },
                      });
                      handleUpdateSuccess();
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    coordinator: userFilter,
    status: { _eq: "active" },
  };

  return (
    <List
      title="Projects"
      storageKey={storageKey}
      endPoint="projects"
      filters={filters}
      columns={columns.filter(
        (col) => !col.hidden && (!col.collection || permissions.hasCollectionPermission(col.collection, "read"))
      )}
      meta={{ fields }}
      href={(item) => ROUTE.PROJECT(item.project_id)}
    />
  );
}
